import { useCallback } from 'react';

import { capture } from '@/analytics';
import { useUserSourceCreate } from '@/hooks/rxdb-models';

import { uploadFile } from '@/services/firebase-storage-service';
import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';

import { getContentType, getFileNameWithoutExtension, UPLOAD_ERROR_MESSAGES, validateFile } from '@/utils/file-upload';

import type { UserSource, UserSourceAvailabilityScope } from '@flintmint/core-shared/rxdb';

export const useUserSourceUpload = (options: UseUserSourceUploadOptions) => {
  const { onProgress, onError, onComplete, availabilityScope } = options;

  const userId = getAuthUser().id;
  const { mutate: createUserSource } = useUserSourceCreate();

  const startUpload = useCallback(
    (item: UploadingItem) => {
      uploadFile(
        userId,
        item.file,
        (progress) => onProgress(item.id, (i) => ({ ...i, progress })),
        () => {
          createUserSource(
            {
              name: getFileNameWithoutExtension(item.file.name),
              sourceType: getContentType(item.file),
              source: `users/${userId}/${item.storageFileName}`,
              sourceDisplayName: item.file.name,
              syncStatus: 'processing',
              ...(availabilityScope && { availabilityScope }),
            },
            {
              onSuccess: (created: UserSource) => {
                capture('user_source_uploaded', { source_type: item.sourceType });
                onComplete(created, item.id);
              },
              onError: () => onError(item.id, (i) => ({ ...i, status: 'error', error: UPLOAD_ERROR_MESSAGES.default })),
            }
          );
        },
        () => onError(item.id, (i) => ({ ...i, status: 'error', error: UPLOAD_ERROR_MESSAGES.default })),
        item.storageFileName
      );
    },
    [userId, createUserSource, onProgress, onError, onComplete]
  );

  const prepareFiles = useCallback((files: FileList | File[] | null): UploadingItem[] => {
    if (!files?.length) return [];

    return Array.from(files).map((file) => {
      const correctedFile = file.type ? file : new File([file], file.name, { type: getContentType(file) });
      const validation = validateFile(correctedFile);

      const id = crypto.randomUUID();

      return {
        kind: 'uploading' as const,
        id,
        name: correctedFile.name,
        storageFileName: `${id}-${correctedFile.name}`,
        sourceType: correctedFile.type,
        progress: 0,
        status: validation.valid ? 'uploading' : 'error',
        error: validation.valid ? null : validation.error,
        file: correctedFile,
      };
    });
  }, []);

  return { startUpload, prepareFiles };
};

interface UseUserSourceUploadOptions {
  onComplete: (created: UserSource, localId: string) => void;
  onError: (id: string, updater: (item: UploadingItem) => UploadingItem) => void;
  onProgress: (id: string, updater: (item: UploadingItem) => UploadingItem) => void;
  availabilityScope?: UserSourceAvailabilityScope;
}

export type UploadingItem = {
  id: string;
  name: string;
  storageFileName: string;
  file: File;
  progress: number;
  kind: 'uploading';
  sourceType?: string | null;
  error?: string | null;
  status: 'uploading' | 'error';
};
